import React from 'react';

import {Link} from 'react-router-dom';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {NavAnchor} from 'src/design/atoms/NavAnchor';
import {Text} from 'src/design/atoms/Text';
import type {
  CourseSessionTrackerResponse} from 'src/api/ProactApiClient';
import {
  SessionStartBlockedReasonEnum,
} from 'src/api/ProactApiClient';
import {useStartCourseAssignmentSessionMutation} from 'src/redux/queries/courseAssignment';
import {Spinner} from 'src/design/atoms/Spinner';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {TrackerActiveSession} from './TrackerActiveSession';
import {TrackerNewSessionCard} from './TrackerNewSessionCard';

export interface CourseSessionContentTrackerProps {
  tracker?: CourseSessionTrackerResponse;
  onSetTracker: (tracker: CourseSessionTrackerResponse) => void;
  onHoldChange?: (held: boolean) => void;
}
export const CourseSessionContentTracker: React.FC<
  CourseSessionContentTrackerProps
> = ({tracker, onSetTracker, onHoldChange}) => {
  const {setAlert} = useAlert();
  const [startCourseAssignmentSession, {isLoading: isStartingNewSession}] =
    useStartCourseAssignmentSessionMutation();

  const isLoading = tracker === undefined || isStartingNewSession;

  const hasActiveSession = tracker?.hasActiveSession === true;
  const canStartNewSession =
    tracker?.hasActiveSession === false && tracker?.canStartSession === true;
  const isBlocked =
    tracker?.hasActiveSession === false && tracker?.canStartSession !== true;

  const handleStartSession = async (language?: string) => {
    try {
      const id = tracker?.assignment.id;
      if (!id) {
        throw new Error('ID should not be null.');
      }

      const resp = await startCourseAssignmentSession({assignmentId: id, language}).unwrap();

      onSetTracker(resp);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const renderBlockedState = () => {
    if (!tracker) {
      return null;
    }

    switch (tracker.startSessionBlockedReason) {
      case SessionStartBlockedReasonEnum.AssignmentComplete:
        return (
          <Alert title="No action required!" color="green">
            <Text>You have already completed this course.</Text>
            <Box mt="md">
              <NavAnchor
                text="Go to Course Overview"
                component={Link}
                to={PathUtility.TrainingCourseOverview(
                  String(tracker.assignment.id)
                )}
              />
            </Box>
          </Alert>
        );
      case SessionStartBlockedReasonEnum.AssignmentLocked:
        return (
          <Alert title="Training locked" color="red">
            <Text>
              It looks like you attempted this course too many times.
              {tracker.assignment.expectedUnlockAt &&
                ` You can try again on ${tracker.assignment.expectedUnlockAt
                  .toString()
                  .toShortDate()}.`}
            </Text>
          </Alert>
        );
      case SessionStartBlockedReasonEnum.ClassroomOnly:
        return (
          <Alert title="This course is completed in a classroom." color="blue">
            <Text>
              There is no online content for this course. Your trainer will
              mark it complete after you attend the class.
            </Text>
            <Box mt="md">
              <NavAnchor
                text="Go to Course Overview"
                component={Link}
                to={PathUtility.TrainingCourseOverview(
                  String(tracker.assignment.id)
                )}
              />
            </Box>
          </Alert>
        );
      default:
        return (
          <Alert title="Unable to start session" color="yellow">
            <Text>You cannot start a session for this course right now.</Text>
          </Alert>
        );
    }
  };

  return isLoading ? (
    <Spinner loadingText="Loading...." />
  ) : (
    <Box>
      {hasActiveSession && (
        <TrackerActiveSession tracker={tracker} onHoldChange={onHoldChange} />
      )}
      {canStartNewSession && (
        <TrackerNewSessionCard
          tracker={tracker}
          onStartNewSession={handleStartSession}
        />
      )}
      {isBlocked && renderBlockedState()}
    </Box>
  );
};
